/* =============================================================================
 * Dates, in the company's own time
 * -----------------------------------------------------------------------------
 * `new Date().toISOString().slice(0, 10)` was used across the app to mean
 * "today". It does not mean today. It means today *in UTC*, and the UAE is four
 * hours ahead — so from midnight until 04:00 Gulf time every screen showed
 * yesterday's date, and a payroll or leave form opened at 01:00 defaulted to
 * the wrong day.
 *
 * It is the kind of fault that looks like the date control is broken, because
 * from the outside that is exactly what it looks like: a date that is correct
 * in the afternoon and wrong in the early morning, changing on its own between
 * one session and the next.
 *
 * Everything here formats against a named zone instead. `en-CA` is used
 * deliberately: it is the one common locale whose short date format is already
 * YYYY-MM-DD, so the result needs no reassembly and cannot be scrambled by the
 * running machine's locale.
 * ========================================================================== */

/** Falls back to Gulf time — every company on this system so far is in the UAE. */
export const DEFAULT_TIME_ZONE = 'Asia/Dubai';

/*
 * The signed-in company's zone, held at module level.
 *
 * The alternative is passing a timezone into every call, through components
 * that have no other reason to know about one — and the first call site that
 * forgets reintroduces exactly the bug this file exists to remove. Set once
 * when the session loads; read by default everywhere else.
 */
let activeTimeZone = DEFAULT_TIME_ZONE;

export function setActiveTimeZone(timeZone: string | null | undefined): void {
  activeTimeZone = timeZone?.trim() || DEFAULT_TIME_ZONE;
}

export function getActiveTimeZone(): string {
  return activeTimeZone;
}

/**
 * The calendar date as it currently reads in a given zone, as YYYY-MM-DD.
 *
 * This is what "today" has to mean in an attendance system: the day a man on
 * site would name if asked, not the day it happens to be in Greenwich.
 */
export function todayIn(timeZone: string = activeTimeZone): string {
  return dateStringIn(new Date(), timeZone);
}

/** The calendar date a given instant falls on, in a given zone. */
export function dateStringIn(
  instant: Date,
  timeZone: string = activeTimeZone,
): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(instant);
  } catch {
    // An unknown zone should not take a screen down with it. UTC is wrong by
    // up to a day; a thrown RangeError is wrong by the whole page.
    return instant.toISOString().slice(0, 10);
  }
}

/**
 * Adds days to a YYYY-MM-DD string and returns another one.
 *
 * Done on the string's own parts rather than by adding milliseconds to a Date.
 * A day is not always 86,400,000 ms — across a daylight-saving boundary it is
 * an hour more or less, and "yesterday" computed that way lands on the wrong
 * date. The Gulf has no daylight saving, but leave and payroll dates are
 * compared against records that may not be local for ever.
 */
export function addDays(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return date;

  // UTC arithmetic on a bare calendar date is safe precisely because no zone
  // is involved: it is pure day counting, not a moment in time.
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return shifted.toISOString().slice(0, 10);
}

/** Whole days from `from` to `to`, inclusive of neither end. */
export function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86_400_000);
}

/** The first day of the month a date falls in. */
export function startOfMonth(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

/**
 * The last day of the month a date falls in.
 *
 * Built from day zero of the *next* month in UTC, which is the last day of this
 * one and needs no table of month lengths and no leap-year rule. A range that
 * ends on the 30th of a 31-day month quietly drops a day of somebody's work
 * from every report it filters.
 */
export function endOfMonth(date: string): string {
  const [y, m] = date.slice(0, 7).split('-').map(Number);
  const last = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
  return `${date.slice(0, 7)}-${String(last).padStart(2, '0')}`;
}

/**
 * Is this date today, in the company's zone?
 *
 * Worth having as a function rather than a comparison at each call site: the
 * comparison is only correct when both sides were derived the same way, and
 * that is exactly the assumption that broke.
 */
export function isToday(date: string, timeZone: string = activeTimeZone): boolean {
  return date === todayIn(timeZone);
}

/**
 * A span of time as a man would read it off a stopwatch: "4h 48m 12s".
 *
 * Seconds are shown because this counts up in front of somebody. A figure that
 * moves once a minute looks frozen for fifty-nine seconds at a time, and the
 * person most likely to notice is the one watching his own hours accumulate.
 *
 * Units are dropped from the left as they empty — nobody wants to read
 * "0h 0m 12s" — but never from the right, so the last digit always ticks.
 */
export function formatElapsed(ms: number): string {
  // Negative means the clock moved, not that time ran backwards. A phone whose
  // time is corrected mid-shift would otherwise print a minus sign at somebody.
  const total = Math.max(0, Math.floor(ms / 1000));

  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  const pad = (n: number) => String(n).padStart(2, '0');

  if (hours > 0) return `${hours}h ${pad(minutes)}m ${pad(seconds)}s`;
  if (minutes > 0) return `${minutes}m ${pad(seconds)}s`;
  return `${seconds}s`;
}

/* =============================================================================
 * How long, in words somebody can picture
 * -----------------------------------------------------------------------------
 * "539 days" is a fact and not an answer. Nobody carries a sense of how long 539
 * days is, so it has to be converted in the reader's head every time — and the
 * two questions it gets asked in, when does this vehicle's registration run out
 * and how long has this site been running, both want the same shape: a year and
 * a half, or three months, or nine days.
 *
 * Calendar arithmetic rather than division, because months are not 30 days and a
 * span that says so is wrong by a week over a couple of years.
 * ========================================================================== */

/**
 * "1 year 5 months", "3 months 4 days", "9 days".
 *
 * Two units at most, and never a leading zero unit — "0 years 3 months" is worse
 * than "3 months", and the whole point is that it reads as English. The smallest
 * unit is dropped once the largest is years, because nobody renewing insurance
 * in eighteen months' time cares which day of the week it falls on.
 */
export function describeGap(from: string, to: string): string {
  /* A range that runs backwards is not a negative span, it is a question that
     does not apply — an expiry already past, a project not yet started. Callers
     say "over" or "not started" themselves; without this the arithmetic below
     wraps and cheerfully reports eleven months. */
  if (to <= from) return '0 days';

  const [fy, fm, fd] = from.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = to.split('-').map(Number) as [number, number, number];

  let years = ty - fy;
  let months = tm - fm;
  let days = td - fd;

  /* Borrow a real month's length, not an assumed thirty — and twice if it has
     to. From the 31st to the 1st two months later a single borrow leaves this
     still negative, which is the same guard serviceSpan carries in
     calc/settlement.ts, for the same reason and by the same convention: the
     month borrowed is the one before the end date. */
  let guard = 0;
  while (days < 0 && guard < 2) {
    months -= 1;
    days += new Date(Date.UTC(ty, tm - 1 - guard, 0)).getUTCDate();
    guard += 1;
  }
  if (days < 0) days = 0;

  while (months < 0) {
    years -= 1;
    months += 12;
  }

  const unit = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

  if (years > 0) {
    return months > 0 ? `${unit(years, 'year')} ${unit(months, 'month')}` : unit(years, 'year');
  }
  if (months > 0) {
    return days > 0 ? `${unit(months, 'month')} ${unit(days, 'day')}` : unit(months, 'month');
  }
  // Under a month, days are the only thing left and they are what matters most.
  return unit(Math.max(0, days), 'day');
}

/**
 * "Aug-26" — a month somebody can read at a glance.
 *
 * The company's own convention, in its spreadsheets and its notes, and worth
 * following rather than overriding: `2026-08` sorts beautifully and reads as a
 * part number. This is the form that goes on anything a person reads.
 *
 * It lived privately inside the payroll exports, which is why those already
 * looked right while statements built elsewhere still showed `2026-08`. One
 * copy now.
 *
 * Takes either `YYYY-MM` or a full `YYYY-MM-DD`; the day is ignored.
 */
export function formatMonth(date: string): string {
  const [year, month] = date.split('-');
  if (!year || !month) return date;

  const at = new Date(Date.UTC(Number(year), Number(month) - 1, 1));
  if (Number.isNaN(at.getTime())) return date;

  return `${at.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' })}-${year.slice(2)}`;
}
